/**
 * Format currency amount with spaces as thousands separators
 * @param amount - The numeric amount to format
 * @param currency - The currency symbol (default: 'R')
 * @returns Formatted currency string with spaces as thousands separators
 */
export const formatCurrency = (amount: number | string, currency: string = 'R'): string => {
  const num = Number(amount);
  if (isNaN(num)) return `${currency}0.00`;

  // Use en-US locale (period as decimal separator, comma as thousands)
  // then replace commas with spaces for South African style
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const parts = formatted.split('.');
  const integerPart = parts[0].replace(/,/g, ' ');
  const decimalPart = parts[1] || '00';

  return `${currency}${integerPart}.${decimalPart}`;
};

/**
 * Format a number with spaces as thousands separators (no currency symbol)
 * @param amount - The numeric amount to format
 * @returns Formatted number string with spaces as thousands separators
 */
export const formatNumber = (amount: number | string): string => {
  const num = Number(amount);
  if (isNaN(num)) return '0';

  const formatted = num.toLocaleString('en-US');
  const parts = formatted.split('.');
  const integerPart = parts[0].replace(/,/g, ' ');
  return parts[1] ? `${integerPart}.${parts[1]}` : integerPart;
};

/**
 * Format date to 'Y-M-d' format (e.g., 2026-Jun-29)
 * @param dateString - The date string or Date object to format
 * @returns Formatted date string in 'Y-M-d' format
 */
export const formatDate = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return 'N/A';
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return 'Invalid Date';

  const year = date.getFullYear();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();

  return `${year}-${month}-${day}`;
};

/**
 * Format date and time to 'Y-M-d H:i' format (e.g., 2026-Jun-29 14:30)
 * @param dateString - The date string or Date object to format
 * @returns Formatted date-time string in 'Y-M-d H:i' format
 */
export const formatDateTime = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return 'N/A';
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return 'Invalid Date';

  const year = date.getFullYear();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};
